import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@/lib/axios";
import { isSessionExpired, resetSessionExpiry } from "@/lib/session-expiry";
import { cleanSession, clearStaleSearchFilters } from "@/lib/utils/auth-utils/cleanSession";

export interface CurrentUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  planName: string;
  /**
   * Account-level role, maintained by the credit service off the person's team
   * membership: "admin" once they create a team or are made an admin of one.
   *
   * NOT the same as the per-team role, and not what team screens should gate on —
   * those use `canManage` from the team overview, which also accounts for the
   * person's membership being disabled. This is for shaping the app around
   * somebody who administers a team at all.
   *
   * Optional because older sessions were established before it was sent.
   */
  role?: "user" | "admin";
  /** "disabled" cannot happen for a live session — the server ends it. */
  status?: "active" | "disabled";
  /**
   * Whether this account may use the internal super-admin console at /admin.
   *
   * Granted only by setting `isSuperAdmin: true` on the user document by hand — no
   * API writes it, so there is nothing in the app that can hand it out.
   *
   * USE THIS FOR RENDERING ONLY: showing the nav entry, mounting the console's
   * routes. It is not a security boundary, because everything in this store came
   * from a response the browser could in principle have faked. The real gate is
   * `requireSuperAdmin` on every /api/admin route, which answers 404 to everybody
   * else — so a client that forces this true reaches a console where every request
   * fails.
   */
  isSuperAdmin?: boolean;
  /** Names a customOrderRoles document; null for a customer. Don't gate on this — use customOrderPermissions. */
  customOrderRole?: string | null;
  /** Custom Order permission keys for this user (see lib/utils/custom-order-permissions). Rendering only. */
  customOrderPermissions?: string[];
}

// The session lives in httpOnly cookies, so the client cannot read it — it has
// to ask /api/user/profile. That makes "not authenticated" and "haven't found
// out yet" two different states, and collapsing them into one boolean is what
// let the guard redirect during the first paint of every page load.
// "degraded" is the important one: the profile call failed for a reason that says
// nothing about the session (500, timeout, offline). Only the API actually
// rejecting our credentials means logged out — anything else and we let the user
// into the app, because throwing them to login over a transient blip loses their
// session for no reason.
export type AuthStatus = "unknown" | "authenticated" | "unauthenticated" | "degraded";

interface AuthState {
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  status: AuthStatus;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  currentUser: null,
  isAuthenticated: false,
  status: "unknown",
  loading: false,
  error: null,
};

function extractErrorMessage(error: unknown, fallback: string) {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message || fallback;
}

// The OTP-request endpoints answer with a machine-readable `code`
// (account_not_found, account_exists, otp_cooldown, domain_blocked, ...)
// that the login and sign-up pages branch on, so these reject with it too.
export type OtpRequestError = { message: string; code?: string };

function extractOtpRequestError(error: unknown, fallback: string): OtpRequestError {
  const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
  return { message: extractErrorMessage(error, fallback), code };
}

// Login is two steps: request an OTP, then verify it. Only the second step
// produces a session, so only it belongs in extraReducers below.
export const requestLoginOtp = createAsyncThunk(
  "auth/requestLoginOtp",
  async (payload: { email: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/user/login", payload);
      return response.data as { success: boolean; message: string };
    } catch (error) {
      return rejectWithValue(extractOtpRequestError(error, "Unable to send verification code"));
    }
  }
);

export const verifyLoginOtp = createAsyncThunk(
  "auth/verifyLoginOtp",
  async (payload: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/user/login/verify-otp", payload);
      return response.data.data as CurrentUser;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, "Invalid or expired code"));
    }
  }
);

// Same shape for registration: initiate creates the account and sends an
// OTP, verify activates it and returns a session.
export const registerInitiate = createAsyncThunk(
  "auth/registerInitiate",
  async (
    payload: { email: string; firstName: string; lastName: string; companyName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post("/api/user/register/initiate", payload);
      return response.data as { success: boolean; message: string };
    } catch (error) {
      return rejectWithValue(extractOtpRequestError(error, "Unable to create account"));
    }
  }
);

export const verifyRegisterOtp = createAsyncThunk(
  "auth/verifyRegisterOtp",
  async (payload: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/user/register/verify", payload);
      return response.data.data as CurrentUser;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, "Invalid or expired code"));
    }
  }
);

// Clears the session server-side (revokes the refresh token and expires both
// cookies). It deliberately returns no redirect target: flipping status to
// "unauthenticated" is what moves the user, and AuthGuard is the one place that
// acts on it.
export const logout = createAsyncThunk(
  "auth/logout",
  async (_: unknown) => {
    await api.post("/api/user/logout");
  }
);

export const syncCurrentUserWithAPI = createAsyncThunk(
  "auth/syncCurrentUserWithAPI",
  async (_: void = undefined, { rejectWithValue }) => {
    try {
      const response = await api.get("/api/user/profile");
      return response.data.data as CurrentUser;
    } catch (error) {
      // Trust the interceptor's own verdict rather than re-deriving one from the
      // raw status. It decided with more context than is available here — whether
      // a refresh was attempted and how it failed — whether this failure actually
      // ends the session, and it already acted on that via notifySessionExpired().
      //
      // Re-deriving from `status === 401` used to disagree with it: a 403
      // account_disabled response set `unauthorized: false` here (wrong status to
      // match), landing on "degraded" and *overwriting* the correct
      // "unauthenticated" that the interceptor's own sessionExpired() dispatch had
      // just set moments earlier — so a disabled account ended up rendering the
      // app instead of being sent to login, unless the follow-up logout() call
      // happened to resolve afterwards and re-stamp it. Same signal, one source.
      return rejectWithValue({
        message: extractErrorMessage(error, "Could not load your profile"),
        unauthorized: isSessionExpired(),
      });
    }
  }
);

/**
 * Re-fetches a fresh JWT after billing/plan changes. The server re-signs the
 * token with the updated user record and sets new cookies. Call this after a
 * successful plan upgrade, credit purchase, or any operation that changes the
 * user's planName — the current JWT carries the old planName and subsequent
 * requests would see stale entitlements.
 *
 * NOT called on session expiry — that is a full logout.
 */
export const refetchUserToken = createAsyncThunk(
  "auth/refetchUserToken",
  async (_: void = undefined, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/user/refetch-token");
      return response.data.data as CurrentUser;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, "Could not refresh session"));
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * The terminal end of a session, raised by the axios interceptor rather than
     * by a user action. It only marks state — the redirect is the AuthGuard's
     * job, so there is exactly one place in the app that navigates to login.
     */
    sessionExpired: (state) => {
      cleanSession();
      state.currentUser = null;
      state.isAuthenticated = false;
      state.status = "unauthenticated";
      state.loading = false;
    },
    /**
     * Directly update the currentUser without making an API call.
     * Used after profile updates where the response already contains the fresh data.
     */
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestLoginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Requesting a code creates no session yet — verifyLoginOtp does that.
      .addCase(requestLoginOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as OtpRequestError | undefined)?.message ?? null;
      });

    builder
      .addCase(verifyLoginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isAuthenticated = true;
        state.status = "authenticated";
        // Without this, an expiry earlier in this tab's life would leave the
        // latch set and swallow the next one.
        resetSessionExpiry();
        clearStaleSearchFilters();
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(registerInitiate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // Creates the account and sends a code — no session until it's verified.
      .addCase(registerInitiate.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerInitiate.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as OtpRequestError | undefined)?.message ?? null;
      });

    builder
      .addCase(verifyRegisterOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyRegisterOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isAuthenticated = true;
        state.status = "authenticated";
        resetSessionExpiry();
        clearStaleSearchFilters();
      })
      .addCase(verifyRegisterOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(logout.fulfilled, (state) => {
      cleanSession();
      state.currentUser = null;
      state.isAuthenticated = false;
      state.status = "unauthenticated";
    });

    builder
      .addCase(syncCurrentUserWithAPI.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncCurrentUserWithAPI.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isAuthenticated = true;
        state.status = "authenticated";
        resetSessionExpiry();
      })
      .addCase(syncCurrentUserWithAPI.rejected, (state, action) => {
        state.loading = false;
        state.currentUser = null;
        state.isAuthenticated = false;
        const unauthorized = (action.payload as { unauthorized?: boolean } | undefined)?.unauthorized;
        state.status = unauthorized ? "unauthenticated" : "degraded";
        if (unauthorized) {
          cleanSession();
        }
      });

    builder
      .addCase(refetchUserToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refetchUserToken.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isAuthenticated = true;
        state.status = "authenticated";
        resetSessionExpiry();
      })
      .addCase(refetchUserToken.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { sessionExpired, setCurrentUser } = authSlice.actions;

export default authSlice.reducer;
